/**
 * oneWayCollision.ts
 * One-way (semi-solid) platform resolution and ground-support checks.
 *
 * Extracted from collision.ts to keep files under the 300-line limit.
 * Pure functions — no React imports, no async, no side effects.
 *
 * @module oneWayCollision
 */

import type { AABB } from '../types/entity';
import { aabbOverlaps, type CollisionResult } from './collision';

/**
 * Resolve an entity against one-way (semi-solid) platforms.
 * Only resolves landing from above — the entity can pass through from
 * below, left, or right. This is standard "pass-through platform" behaviour.
 *
 * DECISION: One-way check uses `prevBottomY <= platform.y` — the entity's
 * bottom edge must have been at or above the platform surface last frame.
 * This prevents "snagging" when jumping through from below.
 *
 * @param entityX      - Entity's current X.
 * @param entityY      - Entity's current Y.
 * @param hitbox       - Entity hitbox dimensions (width/height).
 * @param platforms    - One-way platform AABBs.
 * @param vy           - Entity's current vertical velocity.
 * @param prevBottomY  - Entity's bottom Y from the previous frame (before integration).
 * @returns Resolved position and collision flags.
 */
export function resolveOneWayPlatforms(
  entityX: number,
  entityY: number,
  hitbox: AABB,
  platforms: readonly AABB[],
  vy: number,
  prevBottomY: number,
): CollisionResult {
  let x = entityX;
  let y = entityY;
  let landedOnTop = false;
  const hitCeiling = false;
  const hitLeft = false;
  const hitRight = false;

  // DECISION: Reuse a single scratch AABB instead of allocating per-platform.
  // This runs in the fixed-step hot path — avoiding GC pressure.
  const entityBox: AABB = { x, y, width: hitbox.width, height: hitbox.height };

  for (const platform of platforms) {
    entityBox.x = x;
    entityBox.y = y;
    if (!aabbOverlaps(entityBox, platform)) continue;

    // DECISION: Only resolve when falling (vy >= 0) AND entity's feet were
    // at or above the platform top last frame. This allows jumping through
    // from below while landing naturally on top.
    if (vy < 0) continue;
    if (prevBottomY > platform.y + 2) continue; // 2px tolerance for float drift

    // DECISION: Select the highest qualifying platform (smallest platform.y)
    // so overlapping one-way platforms resolve to the topmost surface.
    if (!landedOnTop || platform.y < y + hitbox.height) {
      y = platform.y - hitbox.height;
      landedOnTop = true;
    }
  }

  return { x, y, landedOnTop, hitCeiling, hitLeft, hitRight };
}

/**
 * Check whether at least 40% of Nova's width is supported by a platform.
 * Prevents standing on platform corners with <1px overlap.
 *
 * @param entityX     - Entity's X position.
 * @param entityBottom - Entity's bottom Y (entityY + entityHeight).
 * @param entityWidth - Entity hitbox width.
 * @param platforms   - Solid platform AABBs.
 * @param oneWayPlatforms - One-way platform AABBs.
 * @returns True if the entity has enough surface overlap to count as grounded.
 */
export function hasMinimumGroundSupport(
  entityX: number,
  entityBottom: number,
  entityWidth: number,
  platforms: readonly AABB[],
  oneWayPlatforms: readonly AABB[],
): boolean {
  const novaLeft = entityX;
  const novaRight = entityX + entityWidth;
  const minOverlap = entityWidth * 0.4;

  // DECISION: Iterate both arrays separately instead of spreading into
  // a combined array.  Avoids allocating a new array every physics tick.
  for (const p of platforms) {
    if (Math.abs(entityBottom - p.y) <= 2) {
      const overlapLeft = Math.max(novaLeft, p.x);
      const overlapRight = Math.min(novaRight, p.x + p.width);
      if (overlapRight - overlapLeft >= minOverlap) return true;
    }
  }
  for (const p of oneWayPlatforms) {
    if (Math.abs(entityBottom - p.y) <= 2) {
      const overlapLeft = Math.max(novaLeft, p.x);
      const overlapRight = Math.min(novaRight, p.x + p.width);
      if (overlapRight - overlapLeft >= minOverlap) return true;
    }
  }
  return false;
}
